const TRUE_SET = new Set([true, 1, "1", "true", "on", "enabled", "active"]);
const FALSE_SET = new Set([false, 0, "0", "false", "off", "disabled", "inactive"]);

const WORD_VALIDATION_KEYS = [
  "word_validation_enabled",
  "enable_word_validation",
  "is_word_validation_enabled",
  "validate_word_enabled",
];

const toBooleanOrNull = (value) => {
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (TRUE_SET.has(normalized)) return true;
    if (FALSE_SET.has(normalized)) return false;
  }
  if (TRUE_SET.has(value)) return true;
  if (FALSE_SET.has(value)) return false;
  return null;
};

const pickToggleFromObject = (obj) => {
  if (!obj || typeof obj !== "object") return null;

  for (const key of WORD_VALIDATION_KEYS) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const boolValue = toBooleanOrNull(obj[key]);
      if (boolValue !== null) return boolValue;
    }
  }

  if (obj.setting && typeof obj.setting === "object") {
    const nested = pickToggleFromObject(obj.setting);
    if (nested !== null) return nested;
  }

  if (obj.step && typeof obj.step === "object") {
    const nested = pickToggleFromObject(obj.step);
    if (nested !== null) return nested;
  }

  return null;
};

const getWordValidationEnabled = (configSource, defaultValue = true) => {
  if (Array.isArray(configSource)) {
    for (const item of configSource) {
      const found = pickToggleFromObject(item);
      if (found !== null) return found;
    }
    return defaultValue;
  }

  const found = pickToggleFromObject(configSource);
  return found !== null ? found : defaultValue;
};

const sanitizeInputByType = (value, data) => {
  if (value === null || value === undefined || typeof value !== "string") {
    return value;
  }

  if (data?.question_type === "text" && data?.is_only_number) {
    return value.replace(/[^\d]/g, "");
  }

  if (data?.question_type === "text" && data?.is_only_alphabet) {
    return value.replace(/[^A-Za-z\u0E00-\u0E4F\s]|\u0E3F/g, "");
  }

  return value;
};

const shouldRunWordValidation = (data, globalEnabled = true) => {
  if (!globalEnabled || data?.question_type !== "text") return false;
  return Boolean(data?.is_validate_word || data?.is_only_alphabet);
};

function isValidEnglishWord(word) {
  if (!word || word.length === 0) return false;
  const hasVowel = /[aeiouAEIOU]/.test(word);
  if (!hasVowel) return false;
  if (/^(.)\1+$/i.test(word)) return false;
  if (/^(.{1,3})\1{2,}$/.test(word)) return false;
  return true;
}

function isValidThaiWord(word) {
  if (!word || word.length === 0) return false;
  const thaiConsonants = /[\u0E01-\u0E2E]/;
  const thaiVowels = /[\u0E30-\u0E45]/;
  const thaiToneMarks = /[\u0E48-\u0E4B]/;
  if (!thaiConsonants.test(word)) return false;
  if (/^(.)\1+$/.test(word)) return false;
  if (/^(.{1,3})\1{2,}$/.test(word)) return false;
  const hasVowel = thaiVowels.test(word);
  const hasTone = thaiToneMarks.test(word);
  const uniqueChars = new Set(word).size;
  if (word.length <= 2) return hasVowel || hasTone;
  return uniqueChars >= 2 || hasVowel || hasTone;
}

const validateWordInput = (text) => {
  const trimmed = (text || "").trim();
  if (!trimmed) {
    return { valid: false, message: "กรุณากรอกข้อความ" };
  }

// เช็กสระหรือวรรณยุกต์ "ตัวเดียวกัน" ซ้ำติดกันตั้งแต่ 2 ตัวขึ้นไป (เช่น ืื, ูู, ้้, ่่)
const repeatedVowelsAndTonesRegex = /([\u0E30-\u0E3A\u0E47-\u0E4E])\1+/;

// เช็กพยัญชนะ (ไทย/อังกฤษ) หรือตัวเลข "ตัวเดียวกัน" ซ้ำติดกันตั้งแต่ 3 ตัวขึ้นไป (เช่น ววว, งงง, 555)
const repeatedCharsRegex = /([ก-ฮa-zA-Z])\1{2,}/;

  const tokens = trimmed.split(/[\s\-_,./!?;:]+/).filter((token) => token.length > 0);
  if (tokens.length === 0) {
    return { valid: false, message: "ไม่พบคำที่ตรวจสอบได้" };
  }

  for (const token of tokens) {
    if (repeatedCharsRegex.test(token)) {
      return { valid: false, message: `\"${token}\" ไม่ใช่คำที่ถูกต้อง` };
    }

    const thaiParts = token.match(/[\u0E00-\u0E7F]+/g) || [];
    const engParts = token.match(/[a-zA-Z]+/g) || [];

    for (const part of thaiParts) {
      if (repeatedVowelsAndTonesRegex.test(part)) {
        return { valid: false, message: `\"${part}\" ไม่ใช่คำที่ถูกต้อง` };
      }
    }

    for (const part of [...thaiParts, ...engParts]) {
      const isThai = /[\u0E00-\u0E7F]/.test(part);
      const isValid = isThai ? isValidThaiWord(part) : isValidEnglishWord(part);
      if (!isValid) {
        return { valid: false, message: `\"${part}\" ไม่ใช่คำที่ถูกต้อง` };
      }
    }
  }

  return { valid: true, message: "ข้อความถูกต้อง" };
};

export {
  getWordValidationEnabled,
  sanitizeInputByType,
  shouldRunWordValidation,
  validateWordInput,
};
